import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Copy,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Edit,
  Save,
  Check,
  X,
} from "lucide-react";
import { useFullscreenContext } from "../common/withFullscreen";
import { toast } from "@components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { json } from "@codemirror/lang-json";
import { useKeySearch } from "./useKeySearch";
import { KeySearchBadge } from "./KeySearchBadge";

interface ResponseViewerProps {
  response: any;
  loading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  showScrollButton?: boolean;
  height?: string;
  outputLabels?: {
    primary: string;
    secondary: string;
  };
  onCopy?: () => void;
  isError?: boolean;
  isLoopChild?: boolean;
  totalIterations?: number;
  iterations?: any[];
  nodeType?: string;
  rawOutputData?: any;
  fillParent?: boolean;
  onResponseChange?: (newData: any) => void;
}

const ResponseViewer: React.FC<ResponseViewerProps> = ({
  response,
  loading = false,
  emptyMessage = "No output yet",
  loadingMessage = "Request pending...",
  showScrollButton = true,
  height = "530px",
  outputLabels,
  onCopy,
  isError = false,
  isLoopChild = false,
  totalIterations = 0,
  iterations = [],
  nodeType,
  rawOutputData = [],
  fillParent = false,
  onResponseChange,
}) => {
  const { isFullscreen } = useFullscreenContext();
  const containerHeight = fillParent || isFullscreen ? "100%" : height;

  const responseContainerRef = useRef<HTMLDivElement>(null);
  const switchSectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [isScrollable, setIsScrollable] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState<number>(1);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [collapsedSections, setCollapsedSections] = useState<
    Record<number, boolean>
  >({});
  const [activeTab, setActiveTab] = useState<"output" | "console">("output");
  const [isEditing, setIsEditing] = useState(false);
  const [editedCode, setEditedCode] = useState("");
  const [initialEditedCode, setInitialEditedCode] = useState("");

  // Collapse/expand state for individual objects/arrays inside the
  // colored JSON tree. Keyed by a synthetic path (e.g. "root.foo[2].bar")
  // so each nested node can be toggled independently.
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());
  const editorViewRef = useRef<EditorView | null>(null);
  const togglePath = useCallback((path: string) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }, []);

  const unwrapJsonData = (data: any): any => {
    if (!data) return data;
    if (Array.isArray(data)) {
      return data.map((item) => {
        if (item && typeof item === "object" && "json_data" in item)
          return item.json_data;
        return item;
      });
    }
    if (data && typeof data === "object" && "json_data" in data)
      return data.json_data;
    return data;
  };

  const getSwitchColor = (index: number) => {
    const isEven = index % 2 === 0;
    return isEven
      ? {
          border: "var(--chart-3)",
          bg: "var(--background)",
          text: "var(--foreground)",
        }
      : {
          border: "var(--chart-3)",
          bg: "var(--background)",
          text: "var(--foreground)",
        };
  };

  const isSwitchGroupedResponse = (data: any): boolean =>
    Array.isArray(data) &&
    data.length > 0 &&
    data[0] !== null &&
    typeof data[0] === "object" &&
    "rule" in data[0] &&
    "items" in data[0] &&
    "itemCount" in data[0] &&
    Array.isArray(data[0].items);

  const isIfElseResponse = (data: any): boolean => {
    if (!Array.isArray(data) || data.length !== 2) return false;
    if (!Array.isArray(data[0]) || !Array.isArray(data[1])) return false;
    if (
      (typeof data[0] === "object" && data[0] && "rule" in data[0]) ||
      (typeof data[1] === "object" && data[1] && "rule" in data[1])
    )
      return false;
    return true;
  };

  const displayResponse = React.useMemo(() => {
    if (
      isLoopChild &&
      iterations &&
      iterations.length > 0 &&
      selectedIteration
    ) {
      const iteration = iterations.find(
        (iter: any) => iter.iteration === selectedIteration,
      );
      if (iteration?.output_data) return unwrapJsonData(iteration.output_data);
      return response;
    }
    return response;
  }, [isLoopChild, iterations, selectedIteration, response]);

  const isPythonNode =
    nodeType === "pythonCodeNode" || nodeType === "code_node.python_code";

  const collectLogsDeep = (value: any): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value.flatMap((item) => collectLogsDeep(item));
    }

    if (typeof value === "object") {
      const currentLogs = Array.isArray(value.logs)
        ? value.logs.filter((log: any) => typeof log === "string")
        : [];

      const nestedLogs = Object.values(value).flatMap((nestedValue) =>
        collectLogsDeep(nestedValue),
      );

      return [...currentLogs, ...nestedLogs];
    }

    return [];
  };

  // Extract console logs for the selected iteration if this is a loop child node
  const consoleLogs = React.useMemo(() => {
    if (!isPythonNode) return [];

    const source =
      isLoopChild && iterations?.length > 0 && selectedIteration
        ? iterations.find((iter: any) => iter.iteration === selectedIteration)
            ?.output_data
        : rawOutputData;

    if (!source) return [];

    return collectLogsDeep(source);
  }, [isPythonNode, rawOutputData, iterations, selectedIteration, isLoopChild]);

  React.useEffect(() => {
    setActiveTab("output");
    // Reset collapse state whenever a new response comes in, so stale
    // paths from a previous response's shape don't linger.
    setCollapsedPaths(new Set());
  }, [response]);

  const selectedIterationData = React.useMemo(() => {
    if (
      isLoopChild &&
      iterations &&
      iterations.length > 0 &&
      selectedIteration
    ) {
      return iterations.find(
        (iter: any) => iter.iteration === selectedIteration,
      );
    }
    return null;
  }, [isLoopChild, iterations, selectedIteration]);

  const handleCopy = useCallback(async () => {
    let dataToCopy;
    if (
      isLoopChild &&
      iterations &&
      iterations.length > 0 &&
      selectedIteration
    ) {
      const iteration = iterations.find(
        (iter: any) => iter.iteration === selectedIteration,
      );
      dataToCopy = iteration?.output_data
        ? unwrapJsonData(iteration.output_data)
        : response;
    } else {
      dataToCopy = response;
    }
    if (!dataToCopy) {
      toast({
        title: "Nothing to copy",
        description: "No data available",
        variant: "destructive",
      });
      return;
    }
    try {
      const text =
        typeof dataToCopy === "string"
          ? dataToCopy
          : JSON.stringify(dataToCopy, null, 2);
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description:
          isLoopChild && totalIterations > 0
            ? `Iteration ${selectedIteration} data copied to clipboard`
            : "Response copied to clipboard",
        variant: "info",
        duration: 2000,
      });
      setTimeout(() => {
        onCopy?.();
      }, 100);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  }, [
    isLoopChild,
    iterations,
    selectedIteration,
    response,
    onCopy,
    totalIterations,
  ]);

  const handleSectionCopy = useCallback(async (items: any[], rule: string) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(items, null, 2));
      toast({
        title: "Copied!",
        description: `"${rule}" data copied`,
        variant: "info",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  }, []);

  const toggleSection = useCallback((index: number) => {
    setCollapsedSections((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  // ── Click-to-copy for keys ───────────────────────────────────────────────────
  // Converts our internal synthetic path (e.g. "root.foo[2].bar",
  // "root-true.foo", "switch-0[1].bar") into a {{$json...}} expression and
  // copies it to the clipboard, mirroring InputViewer's drag/copy behavior.
  const buildCopyExpression = (path: string): string => {
    const withoutRoot = path.replace(
      /^(root-true|root-false|switch-\d+|root)/,
      "",
    );
    const cleaned = withoutRoot.startsWith(".")
      ? withoutRoot.slice(1)
      : withoutRoot;
    if (!cleaned) return "{{$json}}";
    return cleaned.startsWith("[")
      ? `{{$json${cleaned}}}`
      : `{{$json.${cleaned}}}`;
  };

  const handleCopyKey = useCallback(
    async (e: React.MouseEvent, path: string) => {
      e.stopPropagation();
      const expression = buildCopyExpression(path);
      try {
        await navigator.clipboard.writeText(expression);
        toast({
          title: "Copied",
          description: expression,
          variant: "success",
          duration: 2000,
        });
      } catch {
        toast({
          title: "Copy failed",
          description: "Unable to copy expression to clipboard.",
          variant: "destructive",
          duration: 2000,
        });
      }
    },
    [],
  );

  useEffect(() => {
    const container = responseContainerRef.current;
    if (!container) {
      setIsScrollable(false);
      return;
    }
    const checkScrollable = () => {
      const scrollable = container.scrollHeight > container.clientHeight;
      setIsScrollable(scrollable);
      if (scrollable) {
        setIsAtBottom(
          container.scrollHeight - container.scrollTop <=
            container.clientHeight + 5,
        );
      }
    };
    checkScrollable();
    const observer = new ResizeObserver(checkScrollable);
    observer.observe(container);
    return () => observer.disconnect();
  }, [displayResponse, collapsedSections, collapsedPaths, isEditing]);

  // IntersectionObserver — highlight active tab as user scrolls
  useEffect(() => {
    if (!isSwitchGroupedResponse(displayResponse)) return;
    const container = responseContainerRef.current;
    if (!container) return;
    const observers: IntersectionObserver[] = [];
    const sectionCount = Array.isArray(displayResponse)
      ? displayResponse.length
      : 0;
    for (let i = 0; i < sectionCount; i++) {
      const el = switchSectionRefs.current[i];
      if (!el) continue;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(i);
        },
        { root: container, threshold: 0.3 },
      );
      obs.observe(el);
      observers.push(obs);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [displayResponse]);

  const handleScroll = () => {
    const container = responseContainerRef.current;
    if (!container) return;
    setIsAtBottom(
      container.scrollHeight - container.scrollTop <=
        container.clientHeight + 5,
    );
  };

  const handleScrollToggle = useCallback(() => {
    const container = responseContainerRef.current;
    if (!container) return;
    if (isAtBottom) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [isAtBottom]);

  const handleEdit = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      if (displayResponse === undefined || displayResponse === null) {
        setEditedCode("");
        setInitialEditedCode("");
      } else {
        const serialized = JSON.stringify(displayResponse, null, 2);
        setEditedCode(serialized);
        setInitialEditedCode(serialized);
      }
      setIsEditing(true);
    } catch (error) {
      console.error("Error preparing data for edit:", error);
      // Fallback for non-serializable data
      const fallback = String(displayResponse);
      setEditedCode(fallback);
      setInitialEditedCode(fallback);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInitialEditedCode("");
  };

  const handleSave = () => {
    try {
      const parsedData = JSON.parse(editedCode);
      onResponseChange?.(parsedData);
      setIsEditing(false);
      setInitialEditedCode("");
      toast({
        title: "Success",
        description: "Output data updated successfully",
        variant: "success",
        duration: 2000,
      });
    } catch (error: any) {
      toast({
        title: "Invalid JSON",
        description: error.message || "Please check your JSON format",
        variant: "destructive",
      });
    }
  };

  // ── Key search — only meaningful for the plain JSON dump (not the
  // switch-grouped / if-else layouts, which have their own nested sections,
  // nor while editing or viewing console logs). ─────────────────────────────
  const isDefaultJsonView =
    !loading &&
    !isEditing &&
    !(isPythonNode && activeTab === "console") &&
    displayResponse !== null &&
    displayResponse !== undefined &&
    !isSwitchGroupedResponse(displayResponse) &&
    !isIfElseResponse(displayResponse);

  const hasUnsavedChanges = isEditing && editedCode !== initialEditedCode;

  const searchableData = React.useMemo(
    () => (isDefaultJsonView ? unwrapJsonData(displayResponse) : null),
    [isDefaultJsonView, displayResponse],
  );

  const keySearch = useKeySearch({
    data: searchableData,
    getContainer: () => responseContainerRef.current,
    enabled: isDefaultJsonView,
  });

  const renderColoredJSON = (
    obj: any,
    path: string = "root",
    indent: number = 0,
    searchActive: boolean = false,
  ): React.ReactNode => {
    const indentStr = "  ".repeat(indent);

    if (obj === null || obj === undefined)
      return (
        <span className="text-muted-foreground italic">
          {obj === null ? "null" : "undefined"}
        </span>
      );

    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span>[]</span>;
      const isCollapsed = collapsedPaths.has(path);
      const maxItems = 20;
      const itemsToShow = obj.length > maxItems ? obj.slice(0, maxItems) : obj;
      const hasMore = obj.length > maxItems;

      if (isCollapsed) {
        return (
          <span
            role="button"
            tabIndex={0}
            className="group cursor-pointer select-none inline-flex items-center gap-1 hover:opacity-80"
            onClick={() => togglePath(path)}
            title="Expand"
          >
            <ChevronRight
              size={14}
              className="text-muted-foreground group-hover:text-primary group-hover:scale-125 transition-transform duration-150"
            />
            <span>[</span>
            <span className="text-muted-foreground italic mx-1">
              {obj.length} {obj.length === 1 ? "item" : "items"}
            </span>
            <span>]</span>
          </span>
        );
      }

      return (
        <>
          <span
            role="button"
            tabIndex={0}
            className="group cursor-pointer select-none inline-flex items-center gap-1 hover:opacity-80"
            onClick={() => togglePath(path)}
            title="Collapse"
          >
            <ChevronDown
              size={14}
              className="text-muted-foreground group-hover:text-primary group-hover:scale-125 transition-transform duration-150"
            />
            <span>[</span>
          </span>
          {itemsToShow.map((item, index) => (
            <div key={index} className="pl-4">
              {renderColoredJSON(
                item,
                `${path}[${index}]`,
                indent + 1,
                searchActive,
              )}
              {index < itemsToShow.length - 1 && <span>,</span>}
            </div>
          ))}
          {hasMore && (
            <div className="pl-4 text-muted-foreground text-xs italic">
              ... {obj.length - maxItems} more items (Total: {obj.length})
            </div>
          )}
          <div>{indentStr}]</div>
        </>
      );
    }

    if (typeof obj === "object") {
      const keys = Object.keys(obj);
      if (keys.length === 0) return <span>{"{}"}</span>;
      const isCollapsed = collapsedPaths.has(path);

      if (isCollapsed) {
        return (
          <span
            role="button"
            tabIndex={0}
            className="group cursor-pointer select-none inline-flex items-center gap-1 hover:opacity-80"
            onClick={() => togglePath(path)}
            title="Expand"
          >
            <ChevronRight
              size={14}
              className="text-muted-foreground group-hover:text-primary group-hover:scale-125 transition-transform duration-150"
            />
            <span>&#123;</span>
            <span className="text-muted-foreground italic mx-1">
              {keys.length} {keys.length === 1 ? "key" : "keys"}
            </span>
            <span>&#125;</span>
          </span>
        );
      }

      return (
        <>
          <span
            role="button"
            tabIndex={0}
            className="group cursor-pointer select-none inline-flex items-center gap-1 hover:opacity-80"
            onClick={() => togglePath(path)}
            title="Collapse"
          >
            <ChevronDown
              size={14}
              className="text-muted-foreground group-hover:text-primary group-hover:scale-125 transition-transform duration-150"
            />
            <span>&#123;</span>
          </span>
          {keys.map((key, index) => {
            const value = obj[key];
            const childPath = `${path}.${key}`;
            const { isMatch, matchClassName, segments } =
              keySearch.getKeyMatchInfo(key, searchActive);
            const dragExpression = buildCopyExpression(childPath);
            const dragTooltip = `Click to copy: ${dragExpression}`;
            const keyText = segments ? (
              <>
                {segments.before}
                <span className={matchClassName}>{segments.match}</span>
                {segments.after}
              </>
            ) : (
              key
            );

            return (
              <div key={key} className="pl-4">
                <span
                  onClick={(e) => handleCopyKey(e, childPath)}
                  data-search-match={isMatch ? "true" : undefined}
                  className="nodrag cursor-pointer hover:bg-lightGray hover:shadow-sm px-1.5 py-0.5 rounded font-semibold select-none transition-all border border-transparent hover:border-primary/40"
                  style={{ color: "var(--primary)" }}
                  title={dragTooltip}
                >
                  &quot;{keyText}&quot;
                </span>
                <span>: </span>
                {typeof value === "string" ? (
                  <span
                    style={{
                      color: isError ? "var(--primary)" : "var(--chart-2)",
                    }}
                  >
                    &quot;{value}&quot;
                  </span>
                ) : typeof value === "number" ? (
                  <span
                    style={{
                      color: isError ? "var(--primary)" : "var(--chart-4)",
                    }}
                  >
                    {value}
                  </span>
                ) : typeof value === "boolean" ? (
                  <span
                    style={{
                      color: isError ? "var(--primary)" : "var(--ndr-orange)",
                    }}
                  >
                    {String(value)}
                  </span>
                ) : (
                  renderColoredJSON(value, childPath, indent + 1, searchActive)
                )}
                {index < keys.length - 1 && <span>,</span>}
              </div>
            );
          })}
          <div>{indentStr}&#125;</div>
        </>
      );
    }

    if (typeof obj === "string")
      return (
        <span style={{ color: isError ? "var(--primary)" : "var(--chart-2)" }}>
          &quot;{obj}&quot;
        </span>
      );
    if (typeof obj === "number")
      return (
        <span style={{ color: isError ? "var(--primary)" : "var(--chart-4)" }}>
          {obj}
        </span>
      );
    if (typeof obj === "boolean")
      return (
        <span
          style={{ color: isError ? "var(--primary)" : "var(--ndr-orange)" }}
        >
          {String(obj)}
        </span>
      );
    return <span>{String(obj)}</span>;
  };

  // Tab bar — rendered OUTSIDE the scroll div so it never scrolls away
  const renderSwitchTabs = (data: any[]) => {
    const scrollToSection = (index: number) => {
      setCollapsedSections((prev) => ({ ...prev, [index]: false }));
      setTimeout(() => {
        switchSectionRefs.current[index]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 50);
    };
    return (
      <div className="flex flex-wrap gap-1 px-4 py-2 border-b flex-shrink-0 bg-background z-10">
        {data.map((ruleOutput: any, index: number) => {
          const c = getSwitchColor(index);
          const isActive = activeSection === index;
          return (
            <button
              key={index}
              onClick={() => scrollToSection(index)}
              className="text-xs px-2 py-1 rounded-md font-medium transition-all hover:opacity-90"
              style={{
                backgroundColor: isActive ? c.border : c.bg,
                color: isActive ? "white" : c.text,
                border: `1px solid ${c.border}`,
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {ruleOutput.rule}
              <span className="ml-1 opacity-70">({ruleOutput.itemCount})</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Sections only — no tab bar inside
  const renderSwitchGrouped = (data: any[]) => (
    <div className="space-y-2 pr-1 pb-10">
      {data.map((ruleOutput: any, index: number) => {
        const c = getSwitchColor(index);
        const isCollapsed = !!collapsedSections[index];
        return (
          <div
            key={ruleOutput.rule || `rule-${index}`}
            ref={(el) => {
              switchSectionRefs.current[index] = el;
            }}
            className="border rounded-lg overflow-hidden"
            style={{ borderLeftWidth: "4px", borderLeftColor: c.border }}
          >
            {/* Header — toggle collapse + copy */}
            <div
              className="px-3 py-2 flex items-center justify-between cursor-pointer select-none"
              style={{ backgroundColor: c.bg }}
              onClick={() => toggleSection(index)}
            >
              <div className="flex items-center gap-2">
                {isCollapsed ? (
                  <ChevronRight size={13} style={{ color: c.text }} />
                ) : (
                  <ChevronDown size={13} style={{ color: c.text }} />
                )}
                <span className="text-xs font-bold" style={{ color: c.text }}>
                  {ruleOutput.rule}
                </span>
                <span className="text-xs text-muted-foreground">
                  {ruleOutput.itemCount}{" "}
                  {ruleOutput.itemCount === 1 ? "item" : "items"}
                </span>
              </div>
              {ruleOutput.items && ruleOutput.items.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionCopy(ruleOutput.items, ruleOutput.rule);
                  }}
                  className="flex items-center gap-1 text-xs transition-all hover:opacity-80"
                  style={{ color: c.text }}
                  title={`Copy "${ruleOutput.rule}" data`}
                >
                  <Copy size={11} /> Copy
                </button>
              )}
            </div>
            {/* Collapsible body — rendered through renderColoredJSON so
                individual objects/arrays inside each switch section are
                also independently collapsible, consistent with the rest
                of the viewer, instead of one flat JSON.stringify dump. */}
            {!isCollapsed &&
              (ruleOutput.items && ruleOutput.items.length > 0 ? (
                <div className="p-3 bg-background">
                  <div className="text-xs font-mono leading-relaxed break-words overflow-x-hidden">
                    {renderColoredJSON(ruleOutput.items, `switch-${index}`)}
                  </div>
                </div>
              ) : (
                <div className="p-3 text-center text-xs text-muted-foreground italic">
                  No items matched this rule
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`border rounded-md nodrag nowheel pointer-events-auto w-full flex flex-col overflow-hidden ${
        isError
          ? "bg-red-50/50 dark:bg-red-950/20 border-red-300 dark:border-red-800"
          : "bg-muted/20"
      }`}
      style={{ height: containerHeight }}
      {...keySearch.containerMouseHandlers}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-1 justify-between px-3 py-2 mb-2 border-b flex-shrink-0 shadow-md z-10 ${
          isError
            ? "bg-red-100/80 dark:bg-red-900/30 border-red-300 dark:border-red-800"
            : ""
        }`}
      >
        <div className="flex items-center gap-3">
          {isError && <AlertCircle size={16} className="text-primary" />}

          {isPythonNode ? (
            <div className="flex border rounded-md overflow-hidden">
              <button
                onClick={() => setActiveTab("output")}
                className={`px-3 py-1 text-xs ${
                  activeTab === "output" ? "bg-primary text-background" : ""
                }`}
              >
                Output
              </button>

              <button
                onClick={() => setActiveTab("console")}
                className={`px-3 py-1 text-xs ${
                  activeTab === "console" ? "bg-primary text-background" : ""
                }`}
              >
                Console
              </button>
            </div>
          ) : (
            <label
              className={`text-sm font-semibold ${
                isError ? "text-primary dark:text-red-300" : ""
              }`}
            >
              {isError
                ? "Error Response"
                : isLoopChild
                  ? "Loop Output"
                  : "Output"}
            </label>
          )}
        </div>
        <div className="flex items-center gap-2 nodrag">
          {onResponseChange && (
            <>
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (hasUnsavedChanges) {
                        handleSave();
                      }
                    }}
                    disabled={!hasUnsavedChanges}
                    className={`transition flex items-center gap-1 text-xs  font-medium  ${
                      isFullscreen
                        ? ""
                        : "h-6 w-6 justify-center rounded border"
                    } ${
                      hasUnsavedChanges
                        ? "text-[var(--network-green)] hover:bg-lightGray  hover:scale-105 transition-transform"
                        : "text-muted-foreground opacity-60"
                    }`}
                    title={
                      hasUnsavedChanges ? "Save changes" : "No changes to save"
                    }
                  >
                    {hasUnsavedChanges ? (
                      <Check size={13} strokeWidth={3} />
                    ) : (
                      <Save size={13} />
                    )}
                    {isFullscreen ? " Save changes" : ""}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCancel();
                    }}
                    className="text-primary hover:text-red-600 transition flex items-center gap-1 text-xs font-medium ml-2"
                    title="Cancel editing"
                  >
                    <X size={13} /> Cancel
                  </button>
                </div>
              ) : (
                activeTab !== "console" && (
                  <button
                    onClick={handleEdit}
                    className="px-2 py-1 rounded whitespace-nowrap  bg-primary/10 text-primary hover:bg-primary/20 transition flex items-center gap-1 text-xs font-semibold border border-primary/20 nodrag"
                    title="Edit output data"
                  >
                    <Edit size={13} /> Edit Response
                  </button>
                )
              )}
            </>
          )}
          {displayResponse !== null &&
            displayResponse !== undefined &&
            !isEditing && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopy();
                }}
                className="text-muted-foreground hover:text-primary transition flex items-center gap-1 text-xs ml-2"
                title={
                  isLoopChild && totalIterations > 0
                    ? `Copy Iteration ${selectedIteration} data`
                    : "Copy response"
                }
                data-testid="copy-response-button"
              >
                <Copy size={13} /> Copy
                {isLoopChild && totalIterations > 0
                  ? ` (Iteration ${selectedIteration})`
                  : ""}
              </button>
            )}
        </div>
      </div>

      {/* Loop iteration selector */}
      {isLoopChild && totalIterations > 0 && !isEditing && (
        <div className="flex items-center justify-between bg-muted p-2 px-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Select
              value={selectedIteration.toString()}
              onValueChange={(value) => setSelectedIteration(Number(value))}
            >
              <SelectTrigger className="w-[150px] h-8 text-xs">
                <SelectValue placeholder="Select iteration" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: totalIterations }, (_, i) => i + 1).map(
                  (iter) => (
                    <SelectItem
                      key={iter}
                      value={iter.toString()}
                      className="text-xs"
                    >
                      <div className="text-xs text-muted-foreground">
                        {iter} of{" "}
                        <span className="font-medium">
                          {totalIterations} items (
                          {selectedIterationData?.items_processed === 1
                            ? "1 item"
                            : `${selectedIterationData?.items_processed} items`}
                          )
                        </span>
                      </div>
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          {selectedIterationData && (
            <div className="flex items-center gap-3">
              <span
                className={`text-xs capitalize px-2 py-1 rounded-xl ${
                  selectedIterationData.status === "success"
                    ? "bg-primaryGreen text-lightGreen"
                    : selectedIterationData.status === "failed"
                      ? "bg-primary text-lightRed"
                      : "text-yellow-600 dark:text-yellow-400"
                }`}
              >
                {selectedIterationData.status}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Switch tab bar — outside scroll div, always visible */}
      {isSwitchGroupedResponse(displayResponse) &&
        !loading &&
        !isEditing &&
        renderSwitchTabs(displayResponse)}

      {/* Scroll area */}
      <div className="relative p-0 w-full flex-1 min-h-0">
        {isDefaultJsonView && (
          <KeySearchBadge
            searchQuery={keySearch.searchQuery}
            onSearchChange={keySearch.setSearchQuery}
            matchCount={keySearch.matchCount}
            currentMatchIndex={keySearch.currentMatchIndex}
            isExpanded={keySearch.isSearchExpanded}
            onMouseEnter={keySearch.onMouseEnter}
            onMouseLeave={keySearch.onMouseLeave}
            onIconClick={keySearch.handleSearchIconClick}
            onKeyDown={keySearch.handleSearchKeyDown}
            onFocus={keySearch.onFocus}
            onBlur={keySearch.onBlur}
            onPrev={keySearch.goToPrevMatch}
            onNext={keySearch.goToNextMatch}
            inputRef={keySearch.searchInputRef}
          />
        )}
        <div
          ref={responseContainerRef}
          onScroll={isEditing ? undefined : handleScroll}
          onWheel={(e) => !isEditing && e.stopPropagation()}
          className={`h-full nodrag nowheel cursor-default pointer-events-auto ${isEditing ? "p-0" : "p-4 overflow-x-hidden overflow-y-auto"}`}
        >
          {loading ? (
            <div className="text-xs text-muted-foreground p-4">
              {loadingMessage}
            </div>
          ) : isEditing ? (
            <div
              className="h-full w-full overflow-hidden flex flex-col pointer-events-auto"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex-1 min-h-0 border-b">
                <CodeMirror
                  value={editedCode}
                  height="100%"
                  theme="dark"
                  onChange={(value) => setEditedCode(value)}
                  onCreateEditor={(view) => {
                    editorViewRef.current = view;
                  }}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    indentOnInput: true,
                    syntaxHighlighting: true,
                    bracketMatching: true,
                    closeBrackets: true,
                  }}
                  extensions={[EditorView.lineWrapping, json()]}
                  className="h-full text-xs font-mono"
                />
              </div>
            </div>
          ) : activeTab === "console" && isPythonNode ? (
            consoleLogs.length > 0 ? (
              <div className="font-mono text-xs space-y-1">
                {consoleLogs.map((log: string, index: number) => (
                  <div
                    key={index}
                    className="border-b border-muted pb-1 break-all font-mono text-xs text-muted-foreground"
                  >
                    {log}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                No console logs available
              </div>
            )
          ) : displayResponse ? (
            <div className="text-xs font-mono leading-relaxed break-words overflow-x-hidden overflow-y-visible">
              {isSwitchGroupedResponse(displayResponse) ? (
                renderSwitchGrouped(displayResponse)
              ) : isIfElseResponse(displayResponse) ? (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-primaryGreen/30">
                      <span className="font-bold text-primaryGreen text-sm">
                        {outputLabels?.primary || "TRUE Results"}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        (
                        {Array.isArray(displayResponse[0])
                          ? displayResponse[0].length
                          : 0}{" "}
                        items)
                      </span>
                    </div>
                    {Array.isArray(displayResponse[0]) &&
                    displayResponse[0].length > 0 ? (
                      renderColoredJSON(
                        unwrapJsonData(displayResponse[0]),
                        "root-true",
                      )
                    ) : (
                      <span className="text-muted-foreground italic">
                        {outputLabels?.primary
                          ? "No items"
                          : "No items matched"}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-primary/30">
                      <span className="font-bold text-primary text-sm">
                        {outputLabels?.secondary || "FALSE Results"}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        (
                        {Array.isArray(displayResponse[1])
                          ? displayResponse[1].length
                          : 0}{" "}
                        items)
                      </span>
                    </div>
                    {Array.isArray(displayResponse[1]) &&
                    displayResponse[1].length > 0 ? (
                      renderColoredJSON(
                        unwrapJsonData(displayResponse[1]),
                        "root-false",
                      )
                    ) : (
                      <span className="text-muted-foreground italic">
                        {outputLabels?.secondary
                          ? "No items"
                          : "No items failed"}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                (() => {
                  keySearch.resetMatchCounter();
                  return renderColoredJSON(
                    unwrapJsonData(displayResponse),
                    "root",
                    0,
                    isDefaultJsonView,
                  );
                })()
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{emptyMessage}</div>
          )}
        </div>

        {showScrollButton &&
          isScrollable &&
          displayResponse &&
          !loading &&
          !isEditing && (
            <button
              onClick={handleScrollToggle}
              className="absolute bottom-0 !right-0 p-2 rounded-full z-10 text-primary bg-gradient-to-br from-primary/80 via-primary/60 to-primary/40 backdrop-blur-sm shadow-lg hover:scale-105 transition-all duration-100 border-2"
              title={isAtBottom ? "Scroll to top" : "Scroll to bottom"}
            >
              {isAtBottom ? (
                <ArrowUp size={14} className="text-primary" />
              ) : (
                <ArrowDown size={14} className="text-primary" />
              )}
            </button>
          )}
      </div>
    </div>
  );
};

const arePropsEqual = (
  prevProps: ResponseViewerProps,
  nextProps: ResponseViewerProps,
) => {
  if (
    prevProps.loading !== nextProps.loading ||
    prevProps.emptyMessage !== nextProps.emptyMessage ||
    prevProps.loadingMessage !== nextProps.loadingMessage ||
    prevProps.showScrollButton !== nextProps.showScrollButton ||
    prevProps.height !== nextProps.height ||
    prevProps.fillParent !== nextProps.fillParent ||
    prevProps.isLoopChild !== nextProps.isLoopChild ||
    prevProps.totalIterations !== nextProps.totalIterations ||
    prevProps.nodeType !== nextProps.nodeType ||
    prevProps.isError !== nextProps.isError ||
    prevProps.onResponseChange !== nextProps.onResponseChange
  )
    return false;

  const prevResponse = prevProps.response
    ? JSON.stringify(prevProps.response)
    : null;
  const nextResponse = nextProps.response
    ? JSON.stringify(nextProps.response)
    : null;
  if (prevResponse !== nextResponse) return false;

  const prevIterations = prevProps.iterations
    ? JSON.stringify(prevProps.iterations)
    : null;
  const nextIterations = nextProps.iterations
    ? JSON.stringify(nextProps.iterations)
    : null;
  if (prevIterations !== nextIterations) return false;

  const prevRawOutputData = prevProps.rawOutputData
    ? JSON.stringify(prevProps.rawOutputData)
    : null;
  const nextRawOutputData = nextProps.rawOutputData
    ? JSON.stringify(nextProps.rawOutputData)
    : null;

  return prevRawOutputData === nextRawOutputData;
};

export default React.memo(ResponseViewer, arePropsEqual);
